import { yupResolver } from '@hookform/resolvers/yup';
import { Checkbox, Modal } from '@mantine/core';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { enqueueSnackbar } from 'notistack';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, Container, TextField } from '@/DesignSystem/Component';
import { useLoggedIn, useRubikaUser, useShadUser } from '@/Module/Auth/Hook';
import { authSetIsTransitioningAction } from '@/Module/Auth/Store/Slice/AuthSlice';
import { APPLICATION_LINK, LINKS } from '@/Module/Core/Constant';
import { enqueueError, yup } from '@/Module/Core/Helper';
import { IconAngleLeft } from '@/Module/Icon';
import { useAppDispatch, useAppSelector } from '@/Module/Store/Hook';
import { SubscriptionDescription } from '@/Module/Subscription/Component';
import { MONTH_PERSIAN } from '@/Module/Subscription/Component/SubscriptionPageContainer/SubscriptionPageTabInner';
import { getCalcPriceSubscriptionAction, postSubscriptionRequestAction, rubikaPaymentRequestAction, shadPaymentRequestAction } from '@/Module/Subscription/Store/Action';
import SubscriptionBuyDescription from './SubscriptionBuyDescription';
interface IFormCode {
  code: string | undefined;
}
const FREE_SUBSCRIPTION = 3;
const SubscriptionBuyForm: FC = () => {
  const dispatch = useAppDispatch();
  const loggedIn = useLoggedIn();
  const isRubikaUser = useRubikaUser();
  const isShadUser = useShadUser();
  const paymentIsLoading = useAppSelector(state => state.subscription.paymentIsLoading);
  const CheckPrice = useAppSelector(state => state.subscription.CheckPrice);
  const router = useRouter();
  const id = String(router.query!.id);
  const plan = useAppSelector(state => state.subscription.planList?.find(item => String(item.id) === String(router.query!.id))!);
  const [showDescription, setShowDescription] = useState(false);
  const [checked, setChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [finalPrice, setFinalPrice] = useState<number>(plan?.price?.price || 0);
  const [discountCode, setDiscountCode] = useState('');
  const schema = useMemo(() => {
    return yup.object({
      code: yup.string().optional()
    });
  }, []);
  const {
    control: controlCode,
    handleSubmit: handleSubmitCode,
    watch: watchSubmitCode
  } = useForm<IFormCode>({
    resolver: yupResolver(schema)
  });
  const isCode = !watchSubmitCode('code');
  if (!plan) return null;
  const toggleChecked = () => setChecked(prev => !prev);
  const displayError = (error: any) => {
    dispatch(authSetIsTransitioningAction(false));
    let validationError = error.data.responseDesc.split(':')[1] || 'مشکلی پیش آمده است! لطفاً با پشتیبانی تماس بگیرید!';
    if (validationError.includes('شما از این کد تخفیف قبلا استفاده کرده اید')) validationError = 'شما پیش‌تر، این از این کد تخفیف استفاده کرده‌اید!';
    if (validationError.includes('کد تخفیف یافت نشد')) validationError = 'کد تخفیف، اشتباه وارد شده است!';
    if (error.data?.responseDesc === 'InvalidToken' || error.data?.responseDesc === 'INVALID_USER_TYPE') validationError = 'اعتبار توکن شما به پایان رسیده است. لطفاً مجدداً وارد برنامه شوید!';
    enqueueError(validationError);
  };
  const onSubmit = handleSubmitCode(information => {
    if (!information.code) {
      enqueueSnackbar({
        variant: 'warning',
        message: 'کد تخفیف وارد نشده است!'
      });
      return;
    }
    dispatch(getCalcPriceSubscriptionAction({
      planId: id,
      discountCode: information.code
    })).unwrap().then(data => {
      if (finalPrice === data.data && finalPrice === 0) {
        enqueueSnackbar({
          variant: 'warning',
          message: 'کد تخفیف هم‌اکنون اعمال شده است!'
        });
      } else if (finalPrice === data.data && finalPrice !== 0) {
        enqueueSnackbar({
          variant: 'error',
          message: 'کد تخفیف مربوط به این اشتراک نیست!'
        });
      } else {
        enqueueSnackbar({
          variant: 'success',
          message: 'کد تخفیف اعمال گردید!'
        });
      }
      setFinalPrice(data.data);
      setDiscountCode(information.code || '');
    }).catch(error => {
      displayError(error);
    });
  });
  const normalPurchase = () => {
    dispatch(postSubscriptionRequestAction({
      planId: id,
      discountCode: discountCode || '',
      redirectUrl: `${APPLICATION_LINK}/${LINKS.SUBSCRIPTION.BUY}`
    })).unwrap().then(data => {
      document.location = data.data;
    }).catch(error => {
      displayError(error);
    });
  };
  const rubikaPurchase = () => {
    dispatch(rubikaPaymentRequestAction({
      subscriptionPlanId: Number(id),
      currencyUnit: 1,
      description: plan?.subscriptionName,
      discountCode: discountCode || ''
    })).unwrap().then(data => {
      if (!data.data?.payUrl && data.data?.status === FREE_SUBSCRIPTION) {
        router.push(`${LINKS.SUBSCRIPTION.BUY}`);
        return;
      }
      document.location = data.data?.payUrl;
    }).catch(error => {
      displayError(error);
    });
  };
  const shadPurchase = () => {
    dispatch(shadPaymentRequestAction({
      subscriptionPlanId: Number(id),
      currencyUnit: 1,
      description: plan?.subscriptionName,
      discountCode: discountCode || ''
    })).unwrap().then(data => {
      if (!data.data?.paymentUrl && data.data?.freemium) {
        router.push(`${LINKS.SUBSCRIPTION.BUY}`);
        return;
      }
      document.location = data.data?.paymentUrl;
    }).catch(error => {
      displayError(error);
    });
  };
  const buyPlan = () => {
    if (!loggedIn) {
      router.push(`${LINKS.AUTH.LOGIN}?redirectUrl=${encodeURI(router.asPath)}`);
      return;
    }
    const purchaseActions = {
      rubika: rubikaPurchase,
      shad: shadPurchase,
      default: normalPurchase
    };

    // eslint-disable-next-line no-nested-ternary
    const userType = isRubikaUser ? 'rubika' : isShadUser ? 'shad' : 'default';
    dispatch(authSetIsTransitioningAction(true));
    purchaseActions[userType]();
  };
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="SubscriptionBuyForm.tsx">
        <meta name="robots" content="noindex, nofollow" data-sentry-element="meta" data-sentry-source-file="SubscriptionBuyForm.tsx" />
      </Head>
      <div className=" bg-main-buy bg-top  bg-no-repeat py-6 sm:py-24">
        <Container className="max-w-[1100px]" data-sentry-element="Container" data-sentry-source-file="SubscriptionBuyForm.tsx">
          <h2 className="mb-6 text-center text-3xl font-extrabold text-blueFive drop-shadow-lightBlue sm:mb-12 sm:text-5xl ">
            پیش فاکتور
          </h2>
          <Box color="secondary" className="block" data-sentry-element="Box" data-sentry-source-file="SubscriptionBuyForm.tsx">
            <div className="flex flex-row justify-between bg-white p-4 text-lg font-extrabold text-blueFive">
              <div> خرید اشتراک</div>
              <Link href={LINKS.SUBSCRIPTION.MAIN} className=" flex items-center font-bold text-orangeFour" data-sentry-element="Link" data-sentry-source-file="SubscriptionBuyForm.tsx">
                <span> تغییر اشتراک</span>
                <IconAngleLeft size="small" data-sentry-element="IconAngleLeft" data-sentry-source-file="SubscriptionBuyForm.tsx" />
              </Link>
            </div>
            <div className="m-4 rounded-lg bg-white p-4">
              <div className=" flex flex-row  justify-between border-b-[1px] py-2 font-semibold   text-blueFive sm:py-6">
                <div className="w-[220px] font-bold text-blackSix sm:w-full  ">نوع اشتراک</div>
                <div className=" w-full">
                  <div className="text-base sm:text-lg">{plan?.subscriptionName}</div>
                  <Button className="text-sm text-orangeFour sm:text-base" onClick={() => setShowDescription(!showDescription)} data-sentry-element="Button" data-sentry-source-file="SubscriptionBuyForm.tsx">
                    محتویات اشتراک
                    <IconAngleLeft size="small" className="rotate-90 scale-x-[-1]" data-sentry-element="IconAngleLeft" data-sentry-source-file="SubscriptionBuyForm.tsx" />
                  </Button>
                  {showDescription && <SubscriptionDescription subscriptionDescription={plan.subscriptionDescription} />}
                </div>
              </div>
              <div className=" flex flex-row  justify-between border-b-[1px] py-4 font-semibold text-blueFive">
                <div className="w-[220px] font-bold text-blackSix sm:w-full">مدت اعتبار</div>
                <div className="w-full text-base sm:text-lg">
                  {plan.duration ? ` اشتراک ${MONTH_PERSIAN[plan.duration!]}` : 'پایان سال تحصیلی'}
                </div>
              </div>
              <div className={plan.id === 182 ? 'hidden' : 'justify-between border-b-[1px] py-4 font-semibold text-blueFive'}>
                <div className="w-full font-bold text-blackSix">کد تخفیف دارید؟</div>
                <form onSubmit={onSubmit} className="relative  flex w-full flex-col bg-white   py-4">
                  <div className="relative  flex flex-row items-center ">
                    <TextField control={controlCode} type="string" className="w-full text-xl placeholder:!text-blueFive " label="کد تـخـفیــف ..." name="code" color="primary" data-sentry-element="TextField" data-sentry-source-file="SubscriptionBuyForm.tsx" />
                    <Button className="!absolute left-[12px] !h-[40px] !rounded-[14px]  font-bold" color="secondary" type="submit" variant="contained" disabled={CheckPrice || isCode} data-sentry-element="Button" data-sentry-source-file="SubscriptionBuyForm.tsx">
                      اعمال
                    </Button>
                  </div>
                </form>
              </div>
              <div className=" flex flex-col justify-between  py-4 font-semibold text-blueFive">
                <div className="w-[220px] font-bold text-blackSix sm:w-full">جزئیات پرداخت</div>
                {!!plan?.price?.originalPrice && <div className="mt-4 flex flex-row justify-between">
                    <div className=" text-sm text-blackFive">قیمت قبل تخفیف</div>
                    <div>
                      <p className="before:contents[''] relative w-fit before:absolute before:right-0 before:top-3 before:h-[1px] before:w-[100px] before:-rotate-6 before:bg-orangeFive  ">
                        {plan?.price?.originalPrice?.toLocaleString()} تومان
                      </p>
                    </div>
                  </div>}
                {!!plan?.price?.originalPrice && <div className="mt-4 flex flex-row justify-between border-b-[1px] pb-4">
                    <div className=" text-sm text-blackFive">قیمت بعد تخفیف</div>
                    <div>
                      <p>{plan?.price?.price?.toLocaleString()} تومان</p>
                    </div>
                  </div>}
                <div className="mt-4 flex flex-row justify-between border-b-[1px] pb-4">
                  <div className=" text-sm text-blackFive">قیمت قابل پرداخت</div>
                  <div className="flex flex-row items-center gap-1 text-xl font-extrabold text-redTwo">
                    {finalPrice?.toLocaleString()}
                    <span className="text-lg">تومان</span>
                  </div>
                </div>
              </div>
              <div>
                <div>
                  <div className="mx-auto flex flex-row justify-center text-center text-sm  font-bold sm:text-base">
                    <Checkbox className="ml-2 flex w-fit" checked={checked} onChange={() => toggleChecked()} data-sentry-element="Checkbox" data-sentry-source-file="SubscriptionBuyForm.tsx" />
                    <Button onClick={() => setShowModal(true)} className="!h-fit !p-0 text-blueFour underline decoration-blueFour decoration-2" data-sentry-element="Button" data-sentry-source-file="SubscriptionBuyForm.tsx">
                      نکات قبل از خرید
                    </Button>
                    <span className="pr-1"> را خوانده‌ام و پذیرفته‌ام.</span>
                  </div>
                </div>
                <div className="  w-full justify-center ">
                  <Button onClick={buyPlan} radius="circle" className=" mb-2 mt-4 w-full min-w-[280px] justify-self-end font-extrabold" color="primary" type="submit" variant="shadow" isLoading={paymentIsLoading} disabled={!checked} data-sentry-element="Button" data-sentry-source-file="SubscriptionBuyForm.tsx">
                    تایید و پرداخت
                  </Button>
                </div>
              </div>
            </div>
          </Box>
          <Modal opened={showModal} size="lg" centered onClose={() => setShowModal(false)} overlayProps={{
          className: '',
          style: {
            backdropFilter: '#0e4791'
          },
          backgroundOpacity: 0.35,
          blur: 9
        }} withCloseButton={false} classNames={{
          root: 'bg-[rgba(14, 71, 145, 0.25)] ',
          content: 'shadow-theme shadow-blackSix bg-white border-2 border-blackSix ',
          inner: 'pl-8'
        }} data-sentry-element="Modal" data-sentry-source-file="SubscriptionBuyForm.tsx">
            <SubscriptionBuyDescription acceptRole={() => {
            setShowModal(false);
            setChecked(true);
          }} data-sentry-element="SubscriptionBuyDescription" data-sentry-source-file="SubscriptionBuyForm.tsx" />
          </Modal>
        </Container>
      </div>
    </>;
};
export default SubscriptionBuyForm;